import React from 'react';
import PropTypes from 'prop-types';
import PanelsDisplayTable from 'app-ui-componentlibrary/dist/componentLibrary/layout/PanelsDisplayTable';
import _ from 'lodash';
import {Header, Loader} from 'semantic-ui-react';
import {connectWithCore} from 'framework/hocs';
import {getPartName, getPartIdWithoutChildrenPath} from 'common/utils';

const getPanelScoreAverages = (data) => {
  let numSessions = _.uniq(_.map(data, (d) => d.sessionId)).length;
  let newData = _.map(data, (o) => {
    let partIdOfLastChild = getPartIdWithoutChildrenPath(o.partId);
    return {...o, partIdOfLastChild: partIdOfLastChild};
  });
  var scoresByPart = _(newData)
    .groupBy('partId')
    .map((objs, key) => {
      return {
        partId: key,
        part: _.find(objs, (o) => o.partId == key).part,
        score: _.sumBy(objs, 'score') / numSessions,
      };
    })
    .value();
  return scoresByPart;
};

const getOverallScore = (panelScore, prescore, weightedAverages) => {
  const factor = weightedAverages.prescore + weightedAverages.panelScore;
  return (
    (panelScore * weightedAverages.panelScore + prescore * weightedAverages.prescore) /
    factor
  );
};

export const OverallPanelScoreSummary = ({
  id,
  refItems,
  prescores,
  scorePart,
  weightedAverages,
  fundingStreams,
  title,
}) => {
  const hasPrescore = prescores && prescores.length > 0;
  const columnHeaders = hasPrescore
    ? ['Activity', 'Panel score average', 'Pre-score', 'Overall score']
    : ['Activity', 'Panel score average', 'Overall score'];

  const columnKeys = hasPrescore
    ? ['activityName', 'panelScoreAvg', 'prescore', 'overallScore']
    : ['activityName', 'panelScoreAvg', 'overallScore'];

  const overallScoresTable = {
    columnHeaders: columnHeaders,
    columnKeys: columnKeys,
    columnWidths: [5, 4, 3, 3],
  };

  const rows = [];
  _.map(getPanelScoreAverages(scorePart), (panelScore, idx) => {
    let partIdWithoutChildPath = getPartIdWithoutChildrenPath(panelScore.partId);
    let activityName = getPartName(panelScore.part, refItems);
    let prescoreWithIndexScore =
      prescores[_.findIndex(prescores, (o) => o.activityId === partIdWithoutChildPath)];
    const prescore = hasPrescore
      ? prescoreWithIndexScore && prescoreWithIndexScore.score
      : 0;
    const overallScore = hasPrescore
      ? getOverallScore(panelScore.score, prescore, weightedAverages)
      : panelScore.score;

    let objToAdd = {
      activityName: activityName,
      panelScoreAvg: _.round(panelScore.score, 2).toFixed(2),
      overallScore: _.round(overallScore, 2).toFixed(2),
      partIdOfLastChild: partIdWithoutChildPath,
      partId: panelScore.partId,
      itemCode: panelScore.part,
    };
    if (hasPrescore) {
      objToAdd = {...objToAdd, prescore: _.round(prescore, 2).toFixed(2)};
    }
    rows.push(objToAdd);
  });

  // get activity names
  let uniqueActivityNames = _.uniq(_.map(rows, (r) => r.activityName));
  // if activityname occurs more than once, update the activity name to include the project name
  _.forEach(uniqueActivityNames, (name) => {
    let nameOccurrances = _.filter(rows, (r) => r.activityName === name);
    if (nameOccurrances && nameOccurrances.length > 1) {
      _.forEach(nameOccurrances, (obj) => {
        let newActivityName = getPartName(
          obj.itemCode,
          refItems,
          obj.partId,
          fundingStreams,
          ' - '
        );
        let idx = _.findIndex(rows, (r) => r.partIdOfLastChild === obj.partIdOfLastChild);
        rows[idx].activityName = newActivityName;
      });
    }
  });

  if (!refItems) {
    return (
      <Loader active inline="centered">
        Loading...
      </Loader>
    );
  } else {
    return _.size(rows) > 0 ? (
      <React.Fragment>
        <Header as="h3" id={id || ''}>
          {title}
        </Header>
        <PanelsDisplayTable
          summary={title}
          columnHeaders={overallScoresTable.columnHeaders}
          columnKeys={overallScoresTable.columnKeys}
          columnWidths={overallScoresTable.columnWidths}
          rows={rows}
        />
      </React.Fragment>
    ) : null;
  }
};

OverallPanelScoreSummary.defaultProps = {
  prescores: [],
  weightedAverages: {
    panelScore: 0.5,
    prescore: 0.5,
  },
  title: 'Overall Panel Score Summary',
};

OverallPanelScoreSummary.propTypes = {
  refItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  prescores: PropTypes.arrayOf(PropTypes.object),
  scorePart: PropTypes.arrayOf(PropTypes.object).isRequired,
  weightedAverages: PropTypes.object,
  title: PropTypes.string,
  fundingStreams: PropTypes.arrayOf(PropTypes.object),
};

export default connectWithCore(OverallPanelScoreSummary);
